// WS-8 P3 — Facturación · Nivel de servicio (vista 3 de 4).
//
// Réplica del Excel "Niveles de servicio (Dashboard)": rollup SEMANAL por
// ACTIVIDAD (área) del cruce por OP. Realizadas = producción capturada del
// líder (con ✓ validada aparte); Cumplimiento REAL ajusta por las unidades
// NO entregadas por el cliente (justificadas al cerrar OPs en el cruce).
//
// Estructura:
//   - Header + selector de período estándar (default: Este mes)
//   - KPIs acumulados del período (Cumplimiento, REAL, unidades)
//   - Gráfico semanal (Cumplimiento vs REAL)
//   - Tabla por semana → filas por actividad + subtotal semanal

import { Fragment, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import {
  inicioSemanaOperativaDeKey,
  type NivelServicioDia,
  type NivelServicioFila,
  type NivelServicioSemana,
} from 'schemas';
import { Button } from '../../components/ui/button';
import { KpiCard } from '../../components/ui/kpi-card';
import { Label } from '../../components/ui/label';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { useNivelServicio } from '../../lib/api/queries';
import { useTenantConfig } from '../../lib/api/useTenantConfig';
import { useAuth } from '../../lib/auth';
import { REGLA_SEMAFORO_COMERCIAL, semaforoComercial } from '../../lib/semaforo';
import { PeriodoSelector, usePeriodoRango } from './factPeriodo';

const fmt = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : n.toLocaleString('es-CO', { maximumFractionDigits: 0 });
const fmtPct = (n: number | null | undefined) =>
  n === null || n === undefined
    ? '—'
    : `${n.toLocaleString('es-CO', { maximumFractionDigits: 1 })}%`;

/** TRUE si la semana operativa NO está completa dentro del rango consultado
 * (QA F4): su % se calcula con menos de 7 días y hay que avisarlo. */
function semanaParcial(
  semanaIso: string,
  desde: string,
  hasta: string,
  diaInicioSemana = 1,
): boolean {
  const inicio = inicioSemanaOperativaDeKey(semanaIso, diaInicioSemana);
  if (inicio === semanaIso) return false; // clave malformada — el helper la devuelve tal cual
  const d = new Date(inicio + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + 6);
  const fin = d.toISOString().slice(0, 10);
  return inicio < desde || fin > hasta;
}

export function FacturacionNivelServicioPage() {
  const { state, signOut } = useAuth();
  const { periodo, setPeriodo, rango, setRango, rangoValido, rangoEfectivo } =
    usePeriodoRango('semana'); // default unificado con Cruce/Programación (QA F3)
  const [clienteId, setClienteId] = useState('');

  const { data, isLoading, isError, error, isFetching } = useNivelServicio({
    desde: rangoEfectivo.desde,
    hasta: rangoEfectivo.hasta,
    cliente_id: clienteId || undefined,
  });
  // Acumulado del AÑO (header del dashboard del cliente), independiente del período.
  const anio = new Date().getFullYear();
  const anual = useNivelServicio({
    desde: `${anio}-01-01`,
    hasta: `${anio}-12-31`,
    cliente_id: clienteId || undefined,
  });

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 p-6">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-tertiary">Cargando nivel de servicio…</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
        <Button onClick={() => signOut()} variant="outline">
          Cerrar sesión
        </Button>
      </main>
    );
  }

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border pb-3">
          <div>
            <h1 className="text-xl font-medium text-fg">Nivel de servicio</h1>
            <p className="text-sm text-fg-tertiary">
              Cumplimiento semanal por actividad. El REAL descuenta lo NO entregado por el cliente
              (justificado al cerrar OPs en el cruce).
            </p>
          </div>
          <PeriodoSelector
            periodo={periodo}
            onPeriodo={setPeriodo}
            rango={rango}
            onRango={setRango}
            rangoValido={rangoValido}
          />
        </header>

        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          <KpiCard
            label="Cumplimiento"
            hint={`Semáforo de compromiso comercial: ${REGLA_SEMAFORO_COMERCIAL}.`}
            value={fmtPct(data.acumulado.cumplimiento_pct)}
            sub={`${fmt(data.acumulado.realizadas)} de ${fmt(data.acumulado.programadas)} unidades`}
            subTone={semaforoComercial(data.acumulado.cumplimiento_pct)}
          />
          <KpiCard
            label="Cumplimiento REAL"
            hint="Ajustado por las unidades NO entregadas por el cliente."
            value={fmtPct(data.acumulado.cumplimiento_real_pct)}
            sub={`${fmt(data.acumulado.no_entregadas)} no entregadas por el cliente`}
            subTone={data.acumulado.no_entregadas > 0 ? 'warn' : 'neutral'}
          />
          <KpiCard
            label="Realizadas"
            value={fmt(data.acumulado.realizadas)}
            sub={`${fmt(data.acumulado.validadas)} validadas ✓`}
          />
          <KpiCard
            label={`Acumulado año ${anio}`}
            hint="Cumplimiento REAL de todo el año (como el header del dashboard)."
            value={fmtPct(anual.data?.acumulado.cumplimiento_real_pct)}
            sub={`${data.semanas.length} semana(s) en el período`}
          />
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ns-cliente">Cliente</Label>
            <Select
              id="ns-cliente"
              value={clienteId}
              onChange={(e) => setClienteId(e.target.value)}
              className="w-44"
            >
              <option value="">Todos</option>
              {data.clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </Select>
          </div>
          {isFetching ? <Spinner className="mb-2 size-4" /> : null}
        </div>

        <TablaNivel
          filas={data.filas}
          semanas={data.semanas}
          desde={data.desde}
          hasta={data.hasta}
        />

        {data.dias.length > 0 ? (
          <SeccionPorcentaje dias={data.dias} semanas={data.semanas} />
        ) : null}
        {data.semanas.length > 0 ? <GraficoSemanal semanas={data.semanas} /> : null}
        {data.filas.length > 0 ? <GraficoPorActividad filas={data.filas} /> : null}

        <footer className="text-center text-[11px] text-fg-tertiary">
          Nivel de servicio · realizadas = producción capturada del líder · ✓ = validada por
          supervisor · REAL = (realizadas + no entregadas del cliente) / programadas
        </footer>
      </div>
    </RoleLayout>
  );
}

// Paleta del sistema de gráficos de la app (patrón C5 valor/costo/% utilidad):
// la serie protagonista va en accent, la referencia en gris, las líneas de %
// en fg-secondary. REAL usa ok (verde), como los tonos positivos de la app.
const SERIES = {
  prog: 'var(--color-fg-tertiary)', // referencia (como "Costo" en C5)
  realiz: 'var(--color-accent)', // protagonista (como "Valor de venta" en C5)
  cumpl: 'var(--color-fg-secondary)', // línea de % (como "% Utilidad" en C5)
  real: 'var(--color-ok)',
};

const ejeCantidad = { fontSize: 10.5, fill: 'var(--color-fg-tertiary)' };
const tooltipStyle = {
  background: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-border)',
  fontSize: 12,
};
const fmtTooltip = (v: unknown, name: unknown) =>
  String(name).includes('%') ? `${String(v ?? '—')}%` : Number(v ?? 0).toLocaleString('es-CO');
const pctLabel = (v: unknown) => (v === null || v === undefined ? '' : `${String(v)}%`);

/** Combo del dashboard: barras prog/realizadas + líneas Cumplimiento y REAL. */
function ComboNivel({
  datos,
  titulo,
}: {
  datos: Array<Record<string, number | string | null>>;
  titulo: string;
}) {
  return (
    <div className="rounded border border-border p-3">
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-fg-tertiary">
        {titulo}
      </h3>
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={datos} margin={{ top: 18, right: 4, bottom: 0, left: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
          <XAxis
            dataKey="etiqueta"
            tick={ejeCantidad}
            axisLine={{ stroke: 'var(--color-border)' }}
          />
          <YAxis
            yAxisId="unds"
            tick={ejeCantidad}
            axisLine={{ stroke: 'var(--color-border)' }}
            tickFormatter={(v: number) => v.toLocaleString('es-CO')}
            width={64}
          />
          <YAxis
            yAxisId="pct"
            orientation="right"
            unit="%"
            domain={[0, 120]}
            tick={ejeCantidad}
            axisLine={{ stroke: 'var(--color-border)' }}
            width={44}
          />
          <Tooltip formatter={fmtTooltip} contentStyle={tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar
            yAxisId="unds"
            dataKey="Programadas"
            fill={SERIES.prog}
            fillOpacity={0.6}
            radius={[3, 3, 0, 0]}
          />
          <Bar
            yAxisId="unds"
            dataKey="Realizadas"
            fill={SERIES.realiz}
            fillOpacity={0.85}
            radius={[3, 3, 0, 0]}
          />
          <Line
            yAxisId="pct"
            type="monotone"
            dataKey="Cumplimiento %"
            stroke={SERIES.cumpl}
            strokeWidth={2}
            dot={{ r: 2 }}
          >
            <LabelList
              dataKey="Cumplimiento %"
              position="bottom"
              formatter={pctLabel}
              style={{ fontSize: 10, fill: 'var(--color-fg-secondary)' }}
            />
          </Line>
          <Line
            yAxisId="pct"
            type="monotone"
            dataKey="Real %"
            stroke={SERIES.real}
            strokeWidth={2}
            dot={{ r: 2 }}
          >
            <LabelList
              dataKey="Real %"
              position="top"
              formatter={pctLabel}
              style={{ fontSize: 10, fill: 'var(--color-ok)', fontWeight: 600 }}
            />
          </Line>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function GraficoSemanal({ semanas }: { semanas: NivelServicioSemana[] }) {
  const datos = semanas.map((s) => ({
    etiqueta: s.semana_iso.slice(6), // número de semana, como el Excel
    Programadas: s.programadas,
    Realizadas: s.realizadas,
    'Cumplimiento %': s.cumplimiento_pct !== null ? Math.round(s.cumplimiento_pct) : null,
    'Real %': s.cumplimiento_real_pct !== null ? Math.round(s.cumplimiento_real_pct) : null,
  }));
  return <ComboNivel datos={datos} titulo="Nivel de servicio / semana" />;
}

const DOW_CORTO = ['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'];
function diaSemana(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '—';
  return DOW_CORTO[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]!;
}

/** Sección "% Nivel de servicio": tabla por DÍA (predeterminada) o gráfico
 * de líneas semanal — pedido del coordinador. */
function SeccionPorcentaje({
  dias,
  semanas,
}: {
  dias: NivelServicioDia[];
  semanas: NivelServicioSemana[];
}) {
  const [vista, setVista] = useState<'tabla' | 'grafico'>('tabla');
  return (
    <div className="rounded border border-border p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-xs font-medium uppercase tracking-wider text-fg-tertiary">
          % Nivel de servicio {vista === 'tabla' ? '/ día' : '/ semana'}
        </h3>
        <SegmentedControl<'tabla' | 'grafico'>
          size="sm"
          value={vista}
          onChange={setVista}
          options={[
            { value: 'tabla', label: 'Tabla' },
            { value: 'grafico', label: 'Gráfico' },
          ]}
        />
      </div>
      {vista === 'grafico' ? (
        <GraficoPorcentajeSemanal semanas={semanas} sinMarco />
      ) : (
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-fg-tertiary">
              <tr>
                <th className="px-2 py-1.5 text-left">Fecha</th>
                <th className="px-2 py-1.5 text-left">Día</th>
                <th className="px-2 py-1.5 text-right">Programadas</th>
                <th className="px-2 py-1.5 text-right">Realizadas</th>
                <th className="px-2 py-1.5 text-right">Validadas ✓</th>
                <th className="px-2 py-1.5 text-right">Cumplimiento</th>
                <th className="px-2 py-1.5 text-right">Cumpl. REAL</th>
              </tr>
            </thead>
            <tbody>
              {dias.map((d) => (
                <tr key={d.fecha} className="border-t border-border">
                  <td className="px-2 py-1.5 tabular-nums">{d.fecha}</td>
                  <td className="px-2 py-1.5 text-xs">{diaSemana(d.fecha)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{fmt(d.programadas)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{fmt(d.realizadas)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-fg-tertiary">
                    {fmt(d.validadas)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-medium">
                    {fmtPct(d.cumplimiento_pct)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-info">
                    {fmtPct(d.cumplimiento_real_pct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** "% NIVEL DE SERVICIO / SEMANA" del dashboard: solo las dos líneas con
 * etiquetas de % — muestra de un vistazo la brecha Cumplimiento vs REAL. */
function GraficoPorcentajeSemanal({
  semanas,
  sinMarco = false,
}: {
  semanas: NivelServicioSemana[];
  sinMarco?: boolean;
}) {
  const datos = semanas.map((s) => ({
    etiqueta: s.semana_iso.slice(6),
    Cumplimiento: s.cumplimiento_pct !== null ? Math.round(s.cumplimiento_pct) : null,
    Real: s.cumplimiento_real_pct !== null ? Math.round(s.cumplimiento_real_pct) : null,
  }));
  return (
    <div className={sinMarco ? '' : 'rounded border border-border p-3'}>
      {sinMarco ? null : (
        <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-fg-tertiary">
          % Nivel de servicio / semana
        </h3>
      )}
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={datos} margin={{ top: 18, right: 12, bottom: 0, left: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
          <XAxis
            dataKey="etiqueta"
            tick={ejeCantidad}
            axisLine={{ stroke: 'var(--color-border)' }}
          />
          <YAxis
            unit="%"
            domain={['dataMin - 10', 110]}
            tick={ejeCantidad}
            axisLine={{ stroke: 'var(--color-border)' }}
            width={44}
          />
          <Tooltip formatter={(v) => `${String(v ?? '—')}%`} contentStyle={tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line
            type="monotone"
            dataKey="Cumplimiento"
            stroke={SERIES.cumpl}
            strokeWidth={2}
            dot={{ r: 2 }}
          >
            <LabelList
              dataKey="Cumplimiento"
              position="bottom"
              formatter={pctLabel}
              style={{ fontSize: 10, fill: 'var(--color-fg-secondary)' }}
            />
          </Line>
          <Line type="monotone" dataKey="Real" stroke={SERIES.real} strokeWidth={2} dot={{ r: 2 }}>
            <LabelList
              dataKey="Real"
              position="top"
              formatter={pctLabel}
              style={{ fontSize: 10, fill: 'var(--color-ok)', fontWeight: 600 }}
            />
          </Line>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Acumulado del período por actividad (el 4º gráfico del dashboard). */
function GraficoPorActividad({ filas }: { filas: NivelServicioFila[] }) {
  const porArea = new Map<
    string,
    { nombre: string; prog: number; realiz: number; noEnt: number }
  >();
  for (const f of filas) {
    const a = porArea.get(f.area_id) ?? { nombre: f.area_nombre, prog: 0, realiz: 0, noEnt: 0 };
    a.prog += f.programadas;
    a.realiz += f.realizadas;
    a.noEnt += f.no_entregadas;
    porArea.set(f.area_id, a);
  }
  const datos = [...porArea.values()].map((a) => ({
    etiqueta: a.nombre,
    Programadas: a.prog,
    Realizadas: a.realiz,
    'Cumplimiento %': a.prog > 0 ? Math.round((a.realiz / a.prog) * 100) : null,
    'Real %': a.prog > 0 ? Math.round(((a.realiz + a.noEnt) / a.prog) * 100) : null,
  }));
  return (
    <ComboNivel datos={datos} titulo="Nivel de servicio / actividad (acumulado del período)" />
  );
}

function TablaNivel({
  filas,
  semanas,
  desde,
  hasta,
}: {
  filas: NivelServicioFila[];
  semanas: NivelServicioSemana[];
  desde: string;
  hasta: string;
}) {
  const { diaInicioSemana } = useTenantConfig();
  if (filas.length === 0) {
    return (
      <div className="rounded border border-dashed border-border p-8 text-center text-sm text-fg-tertiary">
        No hay programación por OP en el período — cargala en Programación por OP.
      </div>
    );
  }

  return (
    <div className="overflow-auto rounded border border-border">
      <table className="w-full text-sm">
        <thead className="sticky top-0 z-10 bg-surface-overlay text-xs text-fg-tertiary">
          <tr>
            <th className="px-3 py-2 text-left">Actividad</th>
            <th className="px-3 py-2 text-right">OPs</th>
            <th className="px-3 py-2 text-right">Programadas</th>
            <th className="px-3 py-2 text-right">Realizadas</th>
            <th className="px-3 py-2 text-right">Validadas ✓</th>
            <th className="px-3 py-2 text-right">Cumplimiento</th>
            <th className="px-3 py-2 text-right">No entregadas</th>
            <th className="px-3 py-2 text-right">% no entr.</th>
            <th className="px-3 py-2 text-right">Cumpl. REAL</th>
          </tr>
        </thead>
        <tbody>
          {semanas.map((sem) => {
            const filasSemana = filas.filter((f) => f.semana_iso === sem.semana_iso);
            return (
              <Fragment key={sem.semana_iso}>
                <tr className="border-t-2 border-border-strong bg-surface-overlay/60 font-medium">
                  <td className="px-3 py-1.5 text-fg-secondary">
                    Semana {sem.semana_iso}
                    {semanaParcial(sem.semana_iso, desde, hasta, diaInicioSemana) ? (
                      <span
                        className="ml-2 text-xs font-normal text-warn"
                        title="La semana no está completa dentro del período consultado — el % se calcula solo con los días incluidos."
                      >
                        parcial
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-1.5" />
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmt(sem.programadas)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmt(sem.realizadas)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmt(sem.validadas)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {fmtPct(sem.cumplimiento_pct)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmt(sem.no_entregadas)}</td>
                  <td className="px-3 py-1.5" />
                  <td className="px-3 py-1.5 text-right tabular-nums text-info">
                    {fmtPct(sem.cumplimiento_real_pct)}
                  </td>
                </tr>
                {filasSemana.map((f) => (
                  <tr key={`${f.semana_iso}-${f.area_id}`} className="border-t border-border">
                    <td className="px-3 py-2">
                      {f.area_codigo} · {f.area_nombre}
                      <span className="text-xs text-fg-tertiary"> — {f.planta_nombre}</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{f.ops}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(f.programadas)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(f.realizadas)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-fg-tertiary">
                      {fmt(f.validadas)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {fmtPct(f.cumplimiento_pct)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(f.no_entregadas)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-fg-tertiary">
                      {fmtPct(f.pct_no_entregadas)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-info">
                      {fmtPct(f.cumplimiento_real_pct)}
                    </td>
                  </tr>
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
